import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Check, X, Wrench } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/**
 * Reactions v0 on a Kuutio-agent message (specs/kuutio-agents/README.md v1; Frans p. 6):
 * accept / reject / fix, then one reason of five for reject and fix. A log, not
 * calibration. `value` is { reaction, reason } or null; `onReact(reaction, reason)`.
 */
const REASONS = ['wrong_owner', 'impossible_mandate', 'tried_already', 'politically_dead', 'wrong_scale'];

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.active ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.22)')};
  background: ${(p) => (p.active ? 'rgba(255,255,255,0.16)' : 'transparent')};
  color: ${(p) => (p.active ? '#fff' : 'rgba(255,255,255,0.65)')};
  font-size: 11px;
  cursor: pointer;
  &:hover { border-color: rgba(255,255,255,0.6); color: #fff; }
`;

const Chip = styled(Btn)`
  padding: 2px 7px;
  font-size: 10.5px;
`;

const Hint = styled.span`
  font-size: 10.5px;
  color: rgba(255,255,255,0.45);
`;

export default function ReactionRow({ value, onReact, disabled = false }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(null); // 'reject' | 'fix' waiting for a reason
  const current = value?.reaction || null;
  const needsReason = pending || ((current === 'reject' || current === 'fix') && !value?.reason ? current : null);

  const pick = (reaction) => {
    if (disabled) return;
    if (reaction === 'accept') { setPending(null); onReact('accept', null); return; }
    setPending(reaction);
  };
  const pickReason = (reason) => {
    const reaction = needsReason;
    setPending(null);
    onReact(reaction, reason);
  };

  return (
    <div>
      <Row>
        <Btn type="button" active={current === 'accept'} onClick={() => pick('accept')} title={t('chatAgent.reactions.acceptHint')}>
          <Check size={12} /> {t('chatAgent.reactions.accept')}
        </Btn>
        <Btn type="button" active={current === 'reject'} onClick={() => pick('reject')} title={t('chatAgent.reactions.rejectHint')}>
          <X size={12} /> {t('chatAgent.reactions.reject')}
        </Btn>
        <Btn type="button" active={current === 'fix'} onClick={() => pick('fix')} title={t('chatAgent.reactions.fixHint')}>
          <Wrench size={12} /> {t('chatAgent.reactions.fix')}
        </Btn>
        {current && value?.reason && !pending && (
          <Hint>· {t(`chatAgent.reactions.reasons.${value.reason}`)}</Hint>
        )}
      </Row>
      {needsReason && (
        <Row>
          <Hint>{t('chatAgent.reactions.why')}</Hint>
          {REASONS.map((r) => (
            <Chip key={r} type="button" active={value?.reason === r && current === needsReason} onClick={() => pickReason(r)}>
              {t(`chatAgent.reactions.reasons.${r}`)}
            </Chip>
          ))}
        </Row>
      )}
    </div>
  );
}
